<template>
  <div class="container" id="carbon-zero">
    <payment-plan @close-popup="closeCarbonZero" v-if="store.state.checkAccess && store.state.component === 'payment-plan'" />
    <Login
      :totalPrice="totalPrice"
      :purchaseRefId="purchaseRefId"
      :purchaseItems="items"
      @close-popup="closeCarbonZero"
      v-if="store.state.checkAccess && store.state.component === 'login'"
    />
    <permission-denied v-if="!store.state.checkAccess && store.state.component === 'un-authorised'" @close-popup="closeCarbonZero" />
    <Loader v-if="store.state.apiCallTracker.inProgress" message="Almost there" :isTransparent="store.state.checkAccess" />

    <exceed-basket-limit v-if="store.state.checkAccess && store.state.component === 'exceed-limit'" @close-popup="closeCarbonZero" />
    <incomplete-registration
      @close-popup="closeCarbonZero"
      v-if="store.state.checkAccess && store.state.component === 'incomplete-registration'"
    />
    <payment-structure @close-popup="closeCarbonZero" v-if="store.state.checkAccess && store.state.component === 'payment-structure'" />
    <session-expired v-if="store.state.isTokenExpired && store.state.component === 'session-expired'" @close-popup="closeCarbonZero" />
    <reviewPayment @close-popup="closeCarbonZero" v-if="store.state.checkAccess && store.state.component === 'review-payment'" />
    <payment-success-modal @close-popup="closeCarbonZero" v-if="store.state.checkAccess && store.state.component === 'success-modal'" />
    <payment-failure-modal @close-popup="closeCarbonZero" v-if="store.state.checkAccess && store.state.component === 'failure-modal'" />
    <change-payment-method
      @close-popup="closeCarbonZero"
      v-if="store.state.checkAccess && store.state.component === 'change-payment-method'"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, onMounted } from 'vue'

import Login from './login/index.vue'
import Loader from '@/components/loaders/index.vue'
import { MerchantItems } from '@/models/composables/index'
import store from '@/composables/useZeroWidgetStore/methods'
import sessionExpired from './session-expired/index.vue'
import permissionDenied from './permission-denied/index.vue'
import ExceedBasketLimit from './exceed-basket-limit/index.vue'
import IncompleteRegistration from './incomplete-registration/index.vue'
import PaymentStructure from './payment-structure/index.vue'
import paymentPlan from './payment-plan/index.vue'
import { checkTokenExpiry } from '@/composables/useUtilities/index'
import reviewPayment from '@/components/review-payment/index.vue'
import paymentSuccessModal from '@/components/payment-success-modal/index.vue'
import paymentFailureModal from '@/components/payment-failure-modal/index.vue'
import GraphikRegular from '@/assets/fonts/Graphik-woff/GraphikRegular.woff'
import GraphikBold from '@/assets/fonts/Graphik-woff/GraphikBold.woff'
import ChangePaymentMethod from '@/components/change-payment-method/index.vue'

import 'vue-skeletor/dist/vue-skeletor.css'

const props = defineProps<{
  totalPrice: number
  purchaseRefId: string
  purchaseItems: string
  merchantId: string
  apiKey: string
  country: string
}>()

const loadFonts = () => {
  const shadowRoot = document.querySelector('.carbon-zero')?.shadowRoot
  const shadowRootStyle = shadowRoot?.querySelector('style') as HTMLStyleElement
  const atFontFaceRule = `
  @font-face {
  font-family: "Graphic Regular";
  src: local("Graphic Regular"), url(${GraphikRegular}) format("woff"),
}
@font-face {
  font-family: "Graphic Bold";
  src: local("Graphic Bold"), url(${GraphikBold}) format("woff"),
}
  `
  const css = `
  ${atFontFaceRule}
h1,
h2,
h3,
h4,
h5,
h6,
.text-title,
.title {
  font-family: 'Graphic Bold', 'Inter', Helvetica, 'Century Gothic', CenturyGothic, AppleGothic, sans-serif;
}
.container {
    font-family: "Graphic Regular",'Inter', Helvetica, 'Segoe UI' , 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}
 `
  const head = document.querySelector('head')
  const linkInter1 = document.createElement('link')
  const linkInter2 = document.createElement('link')
  const linkInter3 = document.createElement('link')
  const headStyle = document.createElement('style')
  linkInter1.rel = 'stylesheet'
  linkInter2.rel = 'preconnect'
  linkInter3.rel = 'preconnect'
  linkInter1.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap'
  linkInter2.href = 'https://fonts.googleapis.com'
  linkInter3.href = 'https://fonts.gstatic.com'
  linkInter3.crossOrigin
  headStyle.textContent = atFontFaceRule
  document.head.appendChild(linkInter1)
  document.head.appendChild(linkInter2)
  document.head.appendChild(linkInter3)
  document.head.appendChild(headStyle)
  const style = document.querySelector('style') as HTMLStyleElement
  style.type = 'text/css'
  if (!style) {
    const styleTag = document.createElement('style')
    styleTag.textContent = css
    head?.appendChild(styleTag)
  } else {
    style.textContent = css + style.textContent
    head?.appendChild(style)
  }
  shadowRootStyle.textContent = css + shadowRootStyle.textContent
}
const items = JSON.parse(props.purchaseItems) as Array<MerchantItems>
const emit = defineEmits<{
  (e: 'close-carbon-zero'): void
}>()
const closeCarbonZero = () => {
  emit('close-carbon-zero')
  location.reload()
}

onMounted(() => {
  loadFonts()
  store.actions.setCustomerPortal(props.apiKey.substring(0, 4))
})

onBeforeMount(() => {
  store.actions.setBaseUrl(props.apiKey)
  store.actions.setZeroWidgetProp({
    totalPrice: props.totalPrice,
    purchaseRefId: props.purchaseRefId,
    purchaseItems: props.purchaseItems,
    merchantId: props.merchantId,
    apiKey: props.apiKey,
    country: props.country
  })
  checkTokenExpiry()

  if (store.state.isTokenExpired) {
    store.actions.setComponent('session-expired')
  }
})
</script>
<style lang="scss">
@use '../assets/styles';
</style>
